import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AppState } from '@app/core/states/app.state';
import { RadioState } from '@app/core/states/radio.state';
import {
  replaceAccents,
  sortNumber,
  sortNumberReverse,
  sortString,
  sortStringReverse,
} from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import {
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import {
  OpenSpecificPanel,
  PANELS_KEYS,
  PanelsState,
  TryActionUnlessPanelIsBlocking,
} from '@radioking/shared/pannel-manager';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';

import { Program } from '../../models/programs.model';
import {
  CreateProgramSuccess,
  HideIntroExtro,
  ProgramsFailure,
  ProgramsRequest,
  ProgramsSuccess,
  StartEditProgram,
  StartProgramDetails,
} from '../../states/programs.actions';
import { ProgramsState } from '../../states/programs.state';
import { DeleteProgramModalComponent } from '../modals/delete-program-modal/delete-program-modal.component';
import { DuplicateProgramModalComponent } from '../modals/duplicate-program-modal/duplicate-program-modal.component';
import { CreateProgramComponent } from '../sides/create-program/create-program.component';

type SortPredicate = 'name' | 'playlistsNumber' | 'estimatedDuration';

@Component({
  selector: 'rk-programs-list',
  templateUrl: './programs-list.component.html',
  styleUrls: ['./programs-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramsListComponent implements OnInit, OnDestroy {
  @Select(ProgramsState.programs)
  programs$: Observable<Program[]>;
  @Select(RadioState.currentRadioId)
  radioId$: Observable<number>;
  @Select(AppState.onMobile)
  onMobile$: Observable<boolean>;

  $programOpen: WritableSignal<Program> = signal(null);
  predicate: SortPredicate = 'name';
  reverse = true;
  originalPrograms: Program[];
  programsObservable$: BehaviorSubject<Program[]> = new BehaviorSubject([]);
  sortedPrograms$: Observable<Program[]>;
  isLoading$: Observable<boolean>;
  searchSubject = new BehaviorSubject<string>('');

  resetChildScrollAxis = new Subject<any>();

  subscription = new Subscription();

  constructor(
    private readonly contextMenuService: ContextMenuService,
    private readonly helper: UiStoreHelperService,
    private readonly dialog: MatDialog,
    private readonly mobileSidenavService: MobileSidenavService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly mobileService: MobileSidenavService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.radioId$
        .pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          this.getPrograms();
        }),
    );
    this.subscription.add(
      this.programs$.subscribe(programs => {
        this.originalPrograms = programs;
        this.programsObservable$.next(programs);
        if (programs) {
          this.sortAll();
        }
      }),
    );

    this.sortedPrograms$ = combineLatest([
      this.programsObservable$,
      this.searchSubject
        .asObservable()
        .pipe(distinctUntilChanged(), debounceTime(200), startWith('')),
    ]).pipe(map(([programs, search]) => this.searchProgram(programs, search)));

    this.isLoading$ = this.helper.getLoadingState(
      ProgramsRequest,
      [ProgramsSuccess, ProgramsFailure],
      true,
    );

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(CreateProgramSuccess))
        .pipe(
          withLatestFrom(this.onMobile$),
          filter(([_, mob]) => mob),
        )
        .subscribe(([prog]) => {
          this.mobileService.close(true);
          this.openProgramDetails(prog.program);
        }),
    );
  }

  searchProgram(programs: Program[], search: string): Program[] {
    return programs.filter(p =>
      replaceAccents(p.name.toLowerCase()).includes(replaceAccents(search.toLowerCase())),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  @Dispatch()
  getPrograms() {
    return new ProgramsRequest();
  }

  onOpenContextMenu(program: Program) {
    this.$programOpen.set(program);
  }

  onCloseContextMenu(scroll = false) {
    this.$programOpen.set(null);
    if (scroll) {
      this.contextMenuService.closeAllContextMenus({ eventType: 'cancel' });
    }
  }

  @Dispatch()
  editProgram(program: Program) {
    return new TryActionUnlessPanelIsBlocking(
      new OpenSpecificPanel(PANELS_KEYS.EDIT_PROGRAM, new StartEditProgram(program)),
    );
  }

  duplicateProgram(program: Program) {
    this.dialog.open(DuplicateProgramModalComponent, { data: program });
  }

  deleteProgram(program: Program) {
    this.dialog.open(DeleteProgramModalComponent, { data: program });
  }

  transition() {
    this.sortAll();
  }

  sortAll() {
    this.sortBy(this.predicate, !this.reverse);
  }

  sortBy(prop: SortPredicate, revert = false) {
    // We do two case since if we add a if in the sort function, we loose performance
    this.programsObservable$.next(this.getSortedArray(prop, revert));
  }

  getSortedArray(prop: SortPredicate, revert = false): Program[] {
    if (prop === 'name') {
      if (revert) {
        return this.originalPrograms.slice().sort(sortStringReverse(prop));
      }

      return this.originalPrograms.slice().sort(sortString(prop));
    }
    if (revert) {
      return this.originalPrograms.slice().sort(sortNumberReverse(prop));
    }

    return this.originalPrograms.slice().sort(sortNumber(prop));
  }

  onChangeSearch(search: string) {
    if (typeof search === 'string') {
      this.searchSubject.next(search);
    }
  }

  touchEvent(program: Program) {
    this.$programOpen.set(program);
  }

  programDetailsPage(radioId: number, program: Program, e: Event) {
    this.onMobile$.pipe(first()).subscribe(mob => {
      // Check if user touch the program but doesn't scroll
      if (mob) {
        // If click on delete button, don't go to details page
        if (e && !!(e.target as HTMLElement).closest('.mob-actions')) {
          return null;
        }

        if (this.$programOpen()?.id === program?.id) {
          return this.openProgramDetails(program);
        }
      } else {
        return this.goToProgram(radioId, program.id);
      }

      return null;
    });
  }

  @Dispatch()
  goToProgram(radioId: number, id: number) {
    return new Navigate(['radio', radioId, 'scheduler', 'programs', 'program', id]);
  }

  @Dispatch()
  openProgramDetails(program: Program) {
    return [
      new StartProgramDetails(program),
      new OpenSpecificPanel(PANELS_KEYS.PROGRAM_DETAILS),
    ];
  }

  createProgram() {
    if (!this.store.selectSnapshot(AppState.onMobile)) {
      const panelsOpened = this.store.selectSnapshot(PanelsState.currentPanel);
      if (panelsOpened === PANELS_KEYS.CREATE_PROGRAM) {
        return;
      }

      this.store.dispatch(
        new TryActionUnlessPanelIsBlocking(
          new OpenSpecificPanel(PANELS_KEYS.CREATE_PROGRAM),
        ),
      );
    } else {
      this.mobileSidenavService.open(CreateProgramComponent, {
        bgType: 'gray',
        closeAction: new HideIntroExtro(),
        noScroll: true,
        hasPadding: false,
      });
    }
  }

  onResetAllScrollAxis() {
    this.resetChildScrollAxis.next(Math.random());
  }

  protected readonly window = window;
}
